import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Row,Col,Form, InputGroup } from 'react-bootstrap';
import React, { useEffect, useRef } from 'react';
import useStore from '../store';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // import styles

export function sanitizePastedPlainText(value = '') {
    return String(value ?? '')
        .replace(/\r\n?/g, '\n')
        .replace(/[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
        .replace(/\t/g, ' ')
        .split('\n')
        .map((line) => line.replace(/[ ]+/g, ' ').trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function insertSanitizedPaste(event, currentValue, onValue) {
    const text = event.clipboardData?.getData('text/plain');
    if (typeof text !== 'string' || text === '') {
        return;
    }

    event.preventDefault();
    const target = event.target;
    const start = target.selectionStart ?? currentValue.length;
    const end = target.selectionEnd ?? currentValue.length;
    const pasted = sanitizePastedPlainText(text);
    const nextValue = `${String(currentValue).slice(0, start)}${pasted}${String(currentValue).slice(end)}`;
    onValue(nextValue);
}

export function appendFormData(formData, data) {
    if (data instanceof Array) {
        data.forEach(item => {
            const { key, value } = item;
            if (value !== null && value !== undefined) {
                formData.append(key, value);
            }
        });
    } else if (typeof data === 'object') {
        for (const key in data) {
            const value = data[key];
            if (value !== null && value !== undefined) {
                formData.append(key, value);
            }
        }
    }
}
export function TextEditor({fieldName}){
    const store = useStore()
    const errors = store.getValue('errors')

    return (
        <div className="mb-5">
            <ReactQuill
              value={store.getValue(fieldName) ||  ''}
              onChange={(content) => {
                store.setValue(fieldName, content);
              }}
            style={{ height: '400px' }}
            />
                   
        </div>
               
      );
}

export function InputText({
    fieldName,
    placeholder,
    icon,
    isLoading,
    type='text',
    readOnly=false,
    onValueChange,
    canMask=false,
    isMasked=false,
    onToggleMask,
    maskOnLabel='Hide',
    maskOffLabel='Show',
}){
    const store = useStore()
    const errors = store.getValue('errors')

    return(<>
                <InputGroup>
                    <InputGroup.Text><i className={`fs-5 ${icon}`}></i></InputGroup.Text>
                    <Form.Control 
                        placeholder={placeholder}
                        type={(canMask && isMasked) ? 'password' : type}
                        value={store.getValue(fieldName) ||  ''}
                        name={fieldName}
                        size='md' 
                        readOnly={isLoading || readOnly}
                        //required 
                        isInvalid={errors?.hasOwnProperty(fieldName)}
                        onChange={ (e) => { 
                          store.setValue(fieldName, e.target.value)
                          if (typeof onValueChange === 'function') {
                            onValueChange(e.target.value)
                          }
                        } }
                    />
                    {canMask && (
                        <InputGroup.Text className="complaint-hide-addon">
                            <button
                                type="button"
                                className="complaint-hide-toggle"
                                disabled={isLoading || readOnly}
                                onClick={() => {
                                    if (typeof onToggleMask === 'function') {
                                        onToggleMask();
                                    }
                                }}
                            >
                                {isMasked ? maskOffLabel : maskOnLabel}
                            </button>
                        </InputGroup.Text>
                    )}
                    {
                        errors?.hasOwnProperty(fieldName) &&
                            (
                                <Form.Control.Feedback type="invalid">   
                                { errors[fieldName] ? errors[fieldName] : null }
                                </Form.Control.Feedback>
                            )
                    }  
                </InputGroup>
            </>)
}

export function InputTextarea({
    fieldName,
    placeholder,
    icon,
    rows,
    isLoading,
    readOnly=false,
    onValueChange,
    autoGrow=false,
}){
    const store = useStore()
    const errors = store.getValue('errors')
    const textareaRef = useRef(null);
    const value = store.getValue(fieldName) || '';

    useEffect(() => {
        if (!autoGrow || !textareaRef.current) {
            return;
        }
        const el = textareaRef.current;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [autoGrow, value]);

    return(<>
                <InputGroup>
                    <InputGroup.Text><i className={`fs-5 ${icon}`}></i></InputGroup.Text>
                    <Form.Control 
                        as="textarea" 
                        ref={textareaRef}
                        rows={rows}
                        placeholder={placeholder}
                        value={value}
                        name={fieldName}
                        size='md' 
                        style={autoGrow ? { overflow: 'hidden', resize: 'vertical' } : undefined}
                        readOnly={isLoading || readOnly}
                        //required 
                        isInvalid={errors?.hasOwnProperty(fieldName)}
                        onChange={ (e) => { 
                          const nextValue = e.target.value
                          store.setValue(fieldName, nextValue)
                          if (typeof onValueChange === 'function') {
                            onValueChange(nextValue)
                          }
                        } }
                        onPaste={(e) => insertSanitizedPaste(e, value, (nextValue) => {
                            store.setValue(fieldName, nextValue);
                            if (typeof onValueChange === 'function') {
                                onValueChange(nextValue);
                            }
                        })}
                    />
                    {
                        errors?.hasOwnProperty(fieldName) &&
                            (
                                <Form.Control.Feedback type="invalid">   
                                { errors[fieldName] ? errors[fieldName] : null }
                                </Form.Control.Feedback>
                            )
                    }  
                </InputGroup>
            </>)
}

export function InputSelect({fieldName, placeholder, icon, isLoading, options, onValueChange, required = true}){
    const store = useStore()
    const errors = store.getValue('errors')

    return(<>
                <InputGroup>
                    <InputGroup.Text><i className={`fs-5 ${icon}`}></i></InputGroup.Text>
                    <Form.Select
                        name={fieldName}
                        size='md' 
                        readOnly={isLoading}
                        required={required}
                        isInvalid={errors?.hasOwnProperty(fieldName)}
                        //value={store.getValue(fieldName) ? store.getValue(fieldName) : '' }  // Parent select manages the selected value
                        value={store.getValue(fieldName) !== undefined && store.getValue(fieldName) !== null
                                ? store.getValue(fieldName)
                                : ''
}
                        onChange={ (e) => { 
                          store.setValue(fieldName, e.target.value)
                          if (typeof onValueChange === 'function') {
                            onValueChange(e.target.value)
                          }
                        } }
                    >
                        <option value="" disabled>{placeholder}</option>
                        {options?.map((option,index) => (
                            <option 
                                value={option.id}
                                key={index}
                                //selected={option.id === store.getValue(fieldName)}  
                            >{option.name}</option>
                        ))}
             
                    </Form.Select>
                    {
                        errors?.hasOwnProperty(fieldName) &&
                            (
                                <Form.Control.Feedback type="invalid">   
                                { errors[fieldName] ? errors[fieldName] : null }
                                </Form.Control.Feedback>
                            )
                    }  
                </InputGroup>
            </>)
}

export function InputFile({fieldName, placeholder, icon,accept='image/*', isLoading}){
    const store = useStore()
    const errors = store.getValue('errors')

    return(<>
                <InputGroup>
                    <InputGroup.Text><i className={`fs-5 ${icon}`}></i></InputGroup.Text>
                    <Form.Control 
                        placeholder={placeholder}
                        type={'file'}
                        //value={store.getValue(fieldName) ||  ''}
                        name={fieldName}
                        size='md' 
                        readOnly={isLoading}
                        accept={accept}
                        isInvalid={errors?.hasOwnProperty(fieldName)}
                        onChange={ (e) => { 
                          store.setValue(fieldName, e.target.files[0])                         
                        } }
                    />
                    {
                        errors?.hasOwnProperty(fieldName) &&
                            (
                                <Form.Control.Feedback type="invalid">   
                                { errors[fieldName] ? errors[fieldName] : null }
                                </Form.Control.Feedback>
                            )
                    }  
                </InputGroup>
            </>)
}

export function InputRadio({fieldName,label,options=[]}){

    const store = useStore()
    const errors = store.getValue('errors')

    return (

        <>
            <Form>
                <Form.Group>
                    <Form.Label><h6>{label}</h6></Form.Label>
                    <Row>
                    {options.map( (item,index) => (
                    
                        <Col key={index}>
                            <Form.Check
                                type="radio"
                                label={item.label}
                                name={fieldName}
                                value={item.value}
                                isInvalid={errors?.hasOwnProperty(fieldName)}
                                checked={store.getValue(fieldName) == item.value }
                                onChange={(e) => { 
                                    store.setValue(fieldName, item.value)
                                }}
                            />
                        </Col>
                    
                    ))}
                    </Row>
                </Form.Group>
            </Form>    
        </>
    )
}

export function InputRadioBoolean({fieldName,icon,label="Active", yesLabel="Yes", noLabel="No"}){
    const store = useStore()
    const errors = store.getValue('errors')

    return(
        <>
        <Form>
            <Form.Group>
                <Form.Label><h6>{label}</h6></Form.Label>
                <Row>
                    <Col>
                    <Form.Check
                        type="radio"
                        label={yesLabel}
                        name={fieldName}
                        value="1"
                        isInvalid={errors?.hasOwnProperty(fieldName)}
                        checked={store.getValue(fieldName) == 1}
                        onChange={(e) => { 
                            store.setValue(fieldName, '1');
                        }}
                    />
                    </Col>
                    <Col>
                    <Form.Check
                        type="radio"
                        label={noLabel}
                        name={fieldName}
                        value="0"
                        isInvalid={errors?.hasOwnProperty(fieldName)}
                        checked={store.getValue(fieldName) == 0}
                        onChange={(e) => { 
                            store.setValue(fieldName,'0');
                        }}
                    />
                    </Col>
                </Row>
                {errors?.hasOwnProperty(fieldName) && (
                    <span className='text-danger'>   
                        {errors[fieldName] ? errors[fieldName] : null}
                    </span>
                )}
                </Form.Group>
            </Form>
        </>
    )
}

export function InputDate({fieldName, icon}){
    const store = useStore()
    const errors = store.getValue('errors')

    return(<>
    
    <InputGroup>
        <InputGroup.Text><i className={`fas ${icon}`}></i></InputGroup.Text>
        <Form.Control 
            name={fieldName}
            type="date" 
            value={store.getValue(fieldName)}
            isInvalid={errors?.hasOwnProperty(fieldName)}
            onChange={(e) => { 
                store.setValue(fieldName, e.target.value ? e.target.value : null);
            }}
            
        />

        {
            errors?.hasOwnProperty(fieldName) &&
                (
                    <Form.Control.Feedback type="invalid">   
                    { errors[fieldName] ? errors[fieldName] : null }
                    </Form.Control.Feedback>
                )
         }  
    </InputGroup>
    </>)
}
